import * as Yup from "yup"
import _ from "lodash"

// Validators
import validation_default from "./validation_default"
import validation_date from "./validation_date"
import validation_text from "./validation_text"
import validation_number from "./validation_number"
import validation_email from "./validation_email"
import validation_phone from "./validation_phone"
import validation_checkbox from "./validation_checkbox"
import validation_dropdown from "./validation_dropdown"
import validation_multi_field from "./validation_multi_field"
import validation_conditional_date from "./validation_conditional_date"
import validation_conditional_text from "./validation_conditional_text"
import validation_conditional_phone from "./validation_conditional_phone"
import validation_conditional_number from "./validation_conditional_number"
import validation_conditional_checkbox from "./validation_conditional_checkbox"
import validation_conditional_radio from "./validation_conditional_radio"
import validation_conditional_dropdown from "./validation_conditional_dropdown"
import validation_staff_password from "./validation_staff_password"
import validation_participant_password from "./validation_participant_password"
import validation_participant_email from "./validation_participant_email"
import validation_activity_length from "./validation_activity_length"
import validation_session_end_time from "./validation_session_end_time"
import validation_form_end_time from "./validation_form_end_time"
import validation_staff_notes from "./validation_staff_notes"
import validation_referral_entity_id from "./validation_referral_entity_id"
import validation_staff_role from "./validation_staff_role"
import validation_staff_primary_supervisor from "./validation_staff_primary_supervisor"
import validation_staff_organization_id from "./validation_staff_organization_id"
import validation_staff_id_selector from "./validation_staff_id_selector"
import validation_manage_services_type from "./validation_manage_services_type"
import validation_demo_ssn from "./validation_demo_ssn"
import validation_program_identity_ids from "./validation_program_identity_ids"
import validation_location_ids from "./validation_location_ids"
import validation_staff_email from "./validation_staff_email"
import validation_assessments from "./validation_assessments"
import validation_multi_programs_ids_by_location from "./validation_multi_programs_ids_by_location"
import validation_multi_organization_ids_by_location from "./validation_multi_organization_ids_by_location"
import validation_multi_organization_ids_by_program from "./validation_multi_organization_ids_by_program"

// --- Helper Functions ---
export const getValidationShapeForField = (field, editMode, validationType) => {
  if (
    field.field_type === "multi_field" ||
    field.field_type === "multi_field_nested"
  ) {
    if (field.multi_fields && field.multi_fields.length > 0) {
      return _.reduce(
        field.multi_fields,
        (shapeAcc, subField) => {
          const subFieldValidation = getValidationShapeForField(
            subField,
            editMode,
            validationType,
          )
          if (
            subField.field_type === "multi_field" ||
            subField.field_type === "multi_field_nested"
          ) {
            return {
              ...shapeAcc,
              [subField.field_name]: Yup.array().of(
                Yup.object().shape(subFieldValidation),
              ),
            }
          } else {
            return { ...shapeAcc, ...subFieldValidation }
          }
        },
        {},
      )
    }
  }
  return getFieldValidation(field, editMode, validationType)
}

const getFieldValidation = (field, editMode, validationType) => {
  const fieldValidation = field.field_validation || null
  const { field_type, field_name } = field

  const skipList = ["hidden", "sys", "service"]
  const skipValidation = _.includes(skipList, field_type)

  const typeSplit = _.split(field_name, "__")
  const fieldName = _.isEmpty(typeSplit[1]) ? field_name : typeSplit[1]

  // Use a 'result' variable (acc) and a 'handled' flag
  // to ensure special cases override standard skips/checks.
  let acc = {}
  let handled = false

  // Special-case validators per field_name
  switch (fieldName) {
    case "staff_membership_id":
      acc = validation_default(field, acc)
      handled = true
      break
    case "staff_account_password":
      acc = validation_staff_password(field, acc, editMode)
      handled = true
      break
    case "participant_account_password":
      acc = validation_participant_password(field, acc, editMode)
      handled = true
      break
    case "participant_email_account":
      acc = validation_participant_email(field, acc, validationType)
      handled = true
      break
    case "activity_length_minutes":
      acc = validation_activity_length(field, acc)
      handled = true
      break
    case "session_end_time":
      acc = validation_session_end_time(field, acc)
      handled = true
      break
    case "form_end_time":
      acc = validation_form_end_time(field, acc)
      handled = true
      break
    case "staff_notes_multiblock":
      acc = validation_staff_notes(field, acc, editMode)
      handled = true
      break
    case "referral_entity_id":
      acc = validation_referral_entity_id(field, acc)
      handled = true
      break
    case "staff_role":
      acc = validation_staff_role(field, acc)
      handled = true
      break
    case "staff_primary_supervisor":
      acc = validation_staff_primary_supervisor(field, acc)
      handled = true
      break
    case "staff_organization_id":
      acc = validation_staff_organization_id(field, acc)
      handled = true
      break
    case "staff_id_selector":
      acc = validation_staff_id_selector(field, acc)
      handled = true
      break
    case "manage_services_type":
      acc = validation_manage_services_type(field, acc)
      handled = true
      break
    case "demo_ssn":
      acc = validation_demo_ssn(field, acc)
      handled = true
      break
    case "program_identity_ids":
      acc = validation_program_identity_ids(field, acc)
      handled = true
      break
    case "location_ids":
      acc = validation_location_ids(field, acc)
      handled = true
      break
    case "staff_email_address":
      acc = validation_staff_email(field, acc)
      handled = true
      break
    case "multi_programs_ids_by_location":
      acc = validation_multi_programs_ids_by_location(field, acc)
      handled = true
      break
    case "multi_organization_ids_by_location":
      acc = validation_multi_organization_ids_by_location(field, acc)
      handled = true
      break
    case "multi_organization_ids_by_program":
      acc = validation_multi_organization_ids_by_program(field, acc)
      handled = true
      break
    case "assessments":
      acc = validation_assessments(fieldName, acc)
      handled = true
      break
    default:
      break
  }

  // Do not skip validation if a special-case validator (handled) ran.
  if ((skipValidation && !handled) || !fieldValidation) {
    return acc
  }

  // If no special case ran, fall back to type-based validators.
  if (!handled) {
    switch (field_type) {
      case "date":
        acc = validation_date(field, acc)
        break
      case "text":
      case "textarea":
      case "textarea_rich":
        acc = validation_text(field, acc)
        break
      case "number":
        acc = validation_number(field, acc)
        break
      case "email":
        acc = validation_email(field, acc)
        break
      case "phone":
        acc = validation_phone(field, acc)
        break
      case "checkbox":
        acc = validation_checkbox(field, acc)
        break
      case "dropdown":
      case "dropdown_us_states":
        acc = validation_dropdown(field, acc)
        break
      case "multi_field":
      case "multi_field_nested":
        acc = validation_multi_field(field, acc)
        break
      case "conditional_date":
        acc = validation_conditional_date(field, acc)
        break
      case "conditional_text":
        acc = validation_conditional_text(field, acc)
        break
      case "conditional_phone":
        acc = validation_conditional_phone(field, acc)
        break
      case "conditional_number":
        acc = validation_conditional_number(field, acc)
        break
      case "conditional_checkbox":
        acc = validation_conditional_checkbox(field, acc)
        break
      case "conditional_radio":
        acc = validation_conditional_radio(field, acc)
        break
      case "conditional_dropdown":
      case "conditional_dropdown_us_states":
        acc = validation_conditional_dropdown(field, acc)
        break
      default:
        acc = validation_default(field, acc)
        break
    }
  }

  // Safety check for broken validators
  if (_.isNil(acc)) {
    console.warn("Validator returned null/undefined", {
      field: field_name,
      fieldObj: field,
    })
    return {}
  }

  return acc
}

// --- Main Export/Logic ---
export default function validationSchema(
  blocks,
  validationType,
  editMode = false,
) {
  const yupObj = _.reduce(
    blocks,
    (acc, block) => {
      const handleValidationPerField = _.reduce(
        block.fields,
        (acc, field) => {
          // Special handling for nested multi-fields (from File #2 structure)
          if (
            field.field_type === "multi_field" ||
            field.field_type === "multi_field_nested"
          ) {
            if (field.multi_fields && field.multi_fields.length > 0) {
              const multiFieldShape = getValidationShapeForField(
                field,
                editMode,
                validationType,
              )
              acc[field.field_name] = Yup.array().of(
                Yup.object().shape(multiFieldShape),
              )
              return acc
            }
          }

          const fieldValidation = getFieldValidation(
            field,
            editMode,
            validationType,
          )
          // Using spread logic from File #2, which is cleaner than _.assign for this context
          return { ...acc, ...fieldValidation }
        },
        {},
      )
      return { ...acc, ...handleValidationPerField }
    },
    {},
  )

  return Yup.object().shape(yupObj)
}
